import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Row, Col, Button, Badge, ModalBody, Spinner } from "reactstrap";
import Head from "../../../layout/head/Head";
import { Block, Icon } from "../../../components/Component";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import {
  Table,
  Collapse as AntCollapse,
  Button as AntButton,
  TableColumnsType,
  Descriptions,
  List,
  Modal,
  Timeline,
  Popover,
  Empty,
  Collapse,
  Space,
} from "antd";
import FormatDate from "../../masters/DateFormater";
import supplierApis from "../../../api/master/supplier";
import costCenterApis from "../../../api/master/costCenter";
import GeneralLedgerApis from "../../../api/master/generalLedger";
import departments from "../../../api/master/departments";
import deliveryAddressApis from "../../../api/master/deliveryAddress";
import { PaperClipOutlined, DownloadOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";
import { useNavigate } from "react-router";
import usersApis from "../../../api/master/users";
import boqApis from "../../../api/boq/boq";
import sitebreakdownMasterApis from "../../../api/master/sitebreakdownMaster";
import { useSelector } from "react-redux";

interface SelectOptions {
  label: string;
  value: string;
}

const { Panel } = AntCollapse;
const ConsumptionTarget_Preview: React.FC = (props: any) => {
  const navigate = useNavigate();
  const { data: sequenceAccess } = useSelector(
    (state: any) => state.sequenceAccess
  );
  const isAutoBoqNumber = sequenceAccess?.includes("BOQ");
  const { watch, register } = useFormContext();
  const [sm, setSm] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const { control, handleSubmit } = useForm();
  const previousFormData = watch();
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [submitData, setSubmitData] = useState<any>("SUBMIT");
  const [sitesOptions, setSitesOptions] = useState<SelectOptions[]>([]);
  const [towerOptions, setTowerOptions] = useState<SelectOptions[]>([]);
  const [flatOptions, setFlatOptions] = useState<SelectOptions[]>([]);
  const [locationOptions, setLocationOptions] = useState<SelectOptions[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const workLevelSetting = previousFormData?.workLevelSetting;

  const loadLocationOptions = useCallback(
    async (siteIdParam?: string | number) => {
      const resolvedSiteId = siteIdParam ?? previousFormData?.siteId;
      if (!resolvedSiteId) {
        setLocationOptions([]);
        return;
      }
      try {
        const response = await sitebreakdownMasterApis.fetchLocationsBySiteId({
          siteId: resolvedSiteId,
        });
        if (response?.status && Array.isArray(response.data)) {
          setLocationOptions(
            response.data.map((loc: any) => ({
              label: loc.locationName,
              value: loc.id?.toString(),
            }))
          );
        } else {
          setLocationOptions([]);
        }
      } catch (error) {
        console.log(error);
        setLocationOptions([]);
      }
    },
    [previousFormData?.siteId]
  );

  // Load all flats when site or tower options are loaded
  useEffect(() => {
    if (previousFormData?.siteId && towerOptions.length > 0) {
      // Load all flats for all towers
      const loadAllFlats = async () => {
        const allFlats: SelectOptions[] = [];
        for (const tower of towerOptions) {
          try {
            const response =
              await sitebreakdownMasterApis.fetchSubUnitsByUnitId({
                siteId: previousFormData.siteId,
                unitId: tower.value,
              });
            if (response?.status && Array.isArray(response.data)) {
              const flats = response.data.map((flat: any) => ({
                label: flat.unitName || flat.subUnitName,
                value: flat.id?.toString(),
              }));
              allFlats.push(...flats);
            }
          } catch (error) {
            console.log(error);
          }
        }
        setFlatOptions(allFlats);
      };
      loadAllFlats();
    } else {
      setFlatOptions([]);
    }
  }, [previousFormData?.siteId, towerOptions]);

  const boqLines = previousFormData?.boqLines || [];

  const boqLineColumns = useMemo(() => {
    const baseColumns: TableColumnsType<any> = [
      {
        title: "BOQ Line Name",
        dataIndex: "boqLineName",
        key: "boqLineName",
        fixed: "left" as const,
        width: 200,
      },
      {
        title: "Contractor",
        dataIndex: "contractorName",
        key: "contractorName",
        width: 180,
        render: (text: any) => text || "--",
      } as TableColumnsType<any>[0],
      ...(workLevelSetting
        ? [
            {
              title: workLevelSetting === "VILLA" ? "Villa" : "Tower / Block",
              dataIndex: "siteStructureUnitName",
              key: "siteStructureUnitName",
              width: 150,
              render: (text: any, record: any) => {
                if (!record.siteStructureUnitId) return "--";
                const unit = towerOptions.find(
                  (item) =>
                    item.value === record.siteStructureUnitId?.toString() ||
                    item.value === record.siteStructureUnitId
                );
                return unit?.label || record.siteStructureUnitName || "--";
              },
            } as TableColumnsType<any>[0],
          ]
        : []),
      ...(workLevelSetting === "FLOOR" || workLevelSetting === "FLAT"
        ? [
            {
              title: "Floor",
              dataIndex: "floorNo",
              key: "floorNo",
              width: 100,
              render: (text: any) => text || "--",
            } as TableColumnsType<any>[0],
          ]
        : []),
      ...(workLevelSetting === "FLAT"
        ? [
            {
              title: "Flat",
              dataIndex: "siteStructureSubUnitName",
              key: "siteStructureSubUnitName",
              width: 150,
              render: (text: any, record: any) => {
                if (!record.siteStructureSubUnitId) return "--";
                const flat = flatOptions.find(
                  (item) =>
                    item.value === record.siteStructureSubUnitId?.toString() ||
                    item.value === record.siteStructureSubUnitId
                );
                return flat?.label || record.siteStructureSubUnitName || "--";
              },
            } as TableColumnsType<any>[0],
          ]
        : []),
      {
        title: "Location",
        dataIndex: "locationName",
        key: "locationName",
        width: 150,
        render: (text: any, record: any) => {
          if (!record.locationId) return "--";
          const location = locationOptions.find(
            (item) =>
              item.value === record.locationId?.toString() ||
              item.value === record.locationId
          );
          return location?.label || record.locationName || "--";
        },
      } as TableColumnsType<any>[0],
    ];

    return baseColumns;
  }, [workLevelSetting, towerOptions, flatOptions, locationOptions]);

  const materialColumns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Category",
      dataIndex: "categoryName",
      key: "categoryName",
      width: 150,
    },
    {
      title: "Material Name",
      dataIndex: "materialName",
      key: "materialName",
      width: 200,
    },
    {
      title: "Standard Qty",
      dataIndex: "quantitySet",
      key: "quantitySet",
      width: 100,
    },
    {
      title: "UOM",
      dataIndex: "uomName",
      key: "uomName",
      width: 100,
    },
    {
      title: "Standard Price",
      dataIndex: "standardPrice",
      key: "standardPrice",
      width: 120,
      render: (text: any) => text || "--",
    },
  ];

  const fetchSites = async () => {
    try {
      const data = await usersApis.getCurrentUser();
      if (data) {
        let sitesOptions = data?.siteAccessibility?.map((item: any) => ({
          label: item.siteName,
          value: item.siteID.toString(),
        }));
        setSitesOptions(sitesOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchSites();
    if (previousFormData.siteId) {
      loadLocationOptions(previousFormData.siteId);

      // Load tower options
      sitebreakdownMasterApis
        .fetchUnitsBySiteId({ siteId: previousFormData.siteId })
        .then((response) => {
          if (response?.status && Array.isArray(response.data)) {
            setTowerOptions(
              response.data.map((unit: any) => ({
                label: unit.unitName,
                value: unit.id?.toString(),
              }))
            );
          }
        });
    }
  }, [loadLocationOptions]);

  const onSubmit = async (data: any) => {
    setDisableSubmit(true);

    // Fetch units to get siteStructureId for each unit
    let unitsData: any[] = [];
    if (previousFormData.siteId) {
      try {
        const response = await sitebreakdownMasterApis.fetchUnitsBySiteId({
          siteId: previousFormData.siteId,
        });
        if (response?.status && Array.isArray(response.data)) {
          unitsData = response.data;
        }
      } catch (error) {
        console.log("Error fetching units:", error);
      }
    }

    // Transform boqLines to lines format for API according to the provided payload structure
    const lines: any[] = [];
    let siteStructureId: number | null = null;

    (previousFormData.boqLines || []).forEach((boqLine: any) => {
      const targetLines: any[] = [];

      (boqLine.materials || []).forEach((material: any) => {
        targetLines.push({
          materialId: material.materialId && Number(material.materialId),
          categoryId: material.categoryId && Number(material.categoryId),
          uomId: material.uomId && Number(material.uomId),
          quantity: material.quantitySet ? Number(material.quantitySet) : 0,
          standardPrice: material.standardPrice ? Number(material.standardPrice) : null,
        });
      });

      // Only add BOQ Line if it has materials
      if (targetLines.length > 0) {
        // Get siteStructureId from unit's siteStructureId property
        if (!siteStructureId && boqLine.siteStructureUnitId) {
          const unit = unitsData.find(
            (u: any) => u.id === Number(boqLine.siteStructureUnitId)
          );
          if (unit && unit.siteStructureId) {
            siteStructureId = Number(unit.siteStructureId);
          }
        }

        // Build line object based on workLevelSetting
        const lineObject: any = {
          boqLineName: boqLine.boqLineName,
          contractorId: boqLine.contractorId
            ? Number(boqLine.contractorId)
            : null,
          siteStructureUnitId: boqLine.siteStructureUnitId
            ? Number(boqLine.siteStructureUnitId)
            : null,
          locationId: boqLine.locationId ? Number(boqLine.locationId) : null,
          targetLines: targetLines,
        };

        // Add floorNo only for FLOOR and FLAT hierarchy
        if (
          previousFormData.workLevelSetting === "FLOOR" ||
          previousFormData.workLevelSetting === "FLAT"
        ) {
          lineObject.floorNo = boqLine.floorNo || null;
        }

        // Add siteStructureSubUnitId only for FLAT hierarchy
        if (previousFormData.workLevelSetting === "FLAT") {
          lineObject.siteStructureSubUnitId = boqLine.siteStructureSubUnitId
            ? Number(boqLine.siteStructureSubUnitId)
            : null;
        }

        lines.push(lineObject);
      }
    });

    // Convert workLevelSetting to segmentLevel format
    const segmentLevelMap: { [key: string]: string } = {
      TOWER_BLOCK: "Tower",
      VILLA: "Villa",
      FLOOR: "Floor",
      FLAT: "Flat",
    };
    const segmentLevel = previousFormData.workLevelSetting
      ? segmentLevelMap[previousFormData.workLevelSetting] ||
        previousFormData.workLevelSetting
      : null;

    // Convert setAlerts to array format
    let setAlertsArray: number[] = [];
    if (previousFormData.setAlerts) {
      if (Array.isArray(previousFormData.setAlerts)) {
        setAlertsArray = previousFormData.setAlerts.map((alert: any) =>
          Number(alert)
        );
      } else {
        setAlertsArray = [Number(previousFormData.setAlerts)];
      }
    }

    let submitData = {
      header: {
        ...(isAutoBoqNumber
          ? {}
          : { targetNumber: previousFormData.targetNumber || null }),
        boqTitle: previousFormData.boqTitle || null,
        siteId: previousFormData.siteId
          ? Number(previousFormData.siteId)
          : null,
        siteStructureId: siteStructureId,
        segmentLevel: segmentLevel,
        limitPostTargetConsumption:
          previousFormData.limitPostTargetConsumption == "Yes" ? 1 : 0,
        limitPostTargetOrder:
          previousFormData.limitPostTargetOrder == "Yes" ? 1 : 0,
        setAlerts: setAlertsArray.length > 0 ? setAlertsArray : null,
        currentAlert: setAlertsArray.length > 0 ? setAlertsArray[0] : null,
        boqUsers: previousFormData.notifyUsers
          ? (Array.isArray(previousFormData.notifyUsers)
              ? previousFormData.notifyUsers
              : [previousFormData.notifyUsers]
            ).map((id: any) => ({ userId: Number(id) }))
          : null,
      },
      lines: lines,
    };
    try {
      const { response, status, message } = await boqApis.newConsumptionTarget(
        submitData
      );
      if (status === true) {
        Swal.fire("Created!", response.targetNumber, "success");
        navigate("/consumption-target-list");
      } else {
        Swal.fire("Error!", message, "error");
        setDisableSubmit(false);
      }
    } catch (error) {
      console.log(error);
      setDisableSubmit(false);
    }
  };

  return (
    <>
      <Head title="New - BOQ " />
      <div className="content clearfix">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Collapse defaultActiveKey={["1", "2"]}>
            <Panel header="General Information" key="1">
              <Descriptions title="" className="custom-description-title">
                <Descriptions.Item label="BOQ Title">
                  {previousFormData.boqTitle || "--"}
                </Descriptions.Item>
                <Descriptions.Item label="BOQ ID">
                  {previousFormData.targetNumber || "--"}
                </Descriptions.Item>
                <Descriptions.Item label="Site">
                  {sitesOptions.find(
                    (item) => item.value == previousFormData.siteId
                  )?.label || "--"}
                </Descriptions.Item>
                <Descriptions.Item label="Site Hierarchy">
                  {previousFormData.workLevelSetting === "TOWER_BLOCK"
                    ? "Tower / Block"
                    : previousFormData.workLevelSetting === "VILLA"
                    ? "Villa"
                    : previousFormData.workLevelSetting === "FLOOR"
                    ? "Floor"
                    : previousFormData.workLevelSetting === "FLAT"
                    ? "Flat"
                    : "Site"}
                </Descriptions.Item>
                <Descriptions.Item label="Limit Material Issue">
                  {previousFormData.limitPostTargetConsumption || "--"}
                </Descriptions.Item>
                <Descriptions.Item label="Limit PO?">
                  {previousFormData.limitPostTargetOrder || "--"}
                </Descriptions.Item>
                <Descriptions.Item label="Consumption Percentage">
                  {previousFormData.setAlerts &&
                  Array.isArray(previousFormData.setAlerts) &&
                  previousFormData.setAlerts.length > 0
                    ? previousFormData.setAlerts.join(", ")
                    : "--"}
                </Descriptions.Item>
                {previousFormData.setAlerts &&
                  Array.isArray(previousFormData.setAlerts) &&
                  previousFormData.setAlerts.length > 0 && (
                    <Descriptions.Item label="Notify Users">
                      {previousFormData.notifyUserNames &&
                      Array.isArray(previousFormData.notifyUserNames) &&
                      previousFormData.notifyUserNames.length > 0
                        ? previousFormData.notifyUserNames.join(", ")
                        : "--"}
                    </Descriptions.Item>
                  )}
              </Descriptions>
            </Panel>
            <Panel header="Material" key="2">
              <div style={{ marginBottom: 20 }}>
                <Table
                  columns={boqLineColumns}
                  dataSource={boqLines.map((boqLine: any, index: number) => ({
                    ...boqLine,
                    key: boqLine?.id?.toString() || `boqLine-${index}`,
                    index,
                  }))}
                  scroll={{ x: 1000 }}
                  expandable={{
                    expandedRowRender: (record: any) => {
                      const materials = record.materials || [];
                      return (
                        <div>
                          <p
                            style={{
                              margin: "0 0 10px 0",
                              fontWeight: "bold",
                            }}
                          >
                            Materials
                          </p>
                          {materials.length === 0 ? (
                            <div
                              style={{
                                padding: "10px",
                                textAlign: "center",
                                color: "#999",
                              }}
                            >
                              <p>No materials added to this BOQ Line.</p>
                            </div>
                          ) : (
                            <div className="mt-3">
                              <Table
                                className="customTable"
                                columns={materialColumns}
                                dataSource={materials.map(
                                  (material: any, index: number) => ({
                                    ...material,
                                    key: index,
                                  })
                                )}
                                scroll={{ x: 800 }}
                                bordered
                                size="small"
                                pagination={false}
                              />
                            </div>
                          )}
                        </div>
                      );
                    },
                    expandedRowKeys,
                    onExpand: (expanded: boolean, record: any) => {
                      if (expanded) {
                        setExpandedRowKeys((prevKeys) => [
                          ...prevKeys,
                          record.key,
                        ]);
                      } else {
                        setExpandedRowKeys((prevKeys) =>
                          prevKeys.filter((key) => key !== record.key)
                        );
                      }
                    },
                    rowExpandable: (record: any) => true,
                  }}
                  bordered
                  size="small"
                  pagination={false}
                  locale={{
                    emptyText:
                      "Please enter BOQ Line Name first, then add materials.",
                  }}
                />
              </div>
            </Panel>
          </Collapse>
          <div className="actions clearfix">
            <ul>
              <li>
                {disableSubmit ? (
                  <Button color="primary" type="button">
                    <Spinner size="sm" color="light" />
                  </Button>
                ) : (
                  <Button color="primary" type="submit">
                    Submit
                  </Button>
                )}
              </li>
              <li>
                <Button
                  color="primary"
                  onClick={() => {
                    props.prev();
                  }}
                  className="btn-dim btn btn-secondary"
                >
                  Previous
                </Button>
              </li>
            </ul>
          </div>
        </form>
      </div>
    </>
  );
};

export default ConsumptionTarget_Preview;
